import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { toast } from "@redotech/redo-web/alert";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { memo, useState } from "react";
import {
  PinVerificationType,
  usePinVerification,
} from "../common/pin-verification-context";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

interface OverrideNotReceivedModalProps {
  isOpen: boolean;
  revert?: boolean;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  onClose: () => void;
}

export const OverrideNotReceivedModal = memo(function OverrideNotReceivedModal({
  isOpen,
  revert = false,
  fulfillmentGroups,
  onClose,
}: OverrideNotReceivedModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { executeWithPinCheck } = usePinVerification();
  const [isLoading, setIsLoading] = useState(false);

  const groupCount = fulfillmentGroups.length;
  const subtitle = revert
    ? `Remove the received override for ${groupCount === 1 ? "this shipment" : `these ${groupCount} shipments`}? Each shipment will return to its underlying fulfillment status.`
    : groupCount === 1
      ? "You are about to override the not received status for this shipment and mark it as open."
      : `You are about to override the not received status for ${groupCount} shipments and mark them as open.`;

  const performOverride = async () => {
    setIsLoading(true);
    try {
      await client.updateFulfillmentGroups({
        updates: fulfillmentGroups.map((group) => ({
          fulfillmentGroupId: group._id,
          overrideAsReceived: !revert,
        })),
      });
      toast(
        `Successfully ${revert ? "reverted the not received override" : "overrode not received status"} for ${groupCount} ${groupCount === 1 ? "shipment" : "shipments"}`,
        { variant: "success" },
      );
      onClose();
    } catch (error) {
      toast(
        revert
          ? "Failed to revert not received override"
          : "Failed to override not received status",
        { variant: "error" },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    executeWithPinCheck(
      performOverride,
      { type: PinVerificationType.OverrideNotReceived },
      {
        title: revert
          ? "Revert Not Received Override"
          : "Override Not Received Status",
        description: revert
          ? "Please enter your PIN to revert the not received override."
          : "Please enter your PIN to override the not received status.",
      },
    );
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Continue",
        onClick: handleConfirm,
        pending: isLoading,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={subtitle}
      title={
        revert ? "Revert not received override" : "Override not received status"
      }
    />
  );
});
